import { useCallback, useEffect, useMemo } from 'react';
import { scheduleActions, scheduleSelectors } from 'features/schedule/redux';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import { type ScheduleConstraintCategory, type ScheduleErrorTimeslot } from 'services/api';
import type { ImprovedScheduleConstraint } from 'features/schedule/redux';
import type { ScheduleConstraintCode } from 'services/api';
import { getColumns } from './columns';

export const useConstraintsTable = () => {
  const dispatch = useAppDispatch();

  const scheduleConstraints = useAppSelector(scheduleSelectors.getConstraints);
  const scheduleViolations = useAppSelector(scheduleSelectors.getViolations);
  const visibleConstraintCodes: ScheduleConstraintCode[] = useMemo(() => {
    return scheduleConstraints
      .filter((constraint) => constraint.isVisible)
      .map((constraint) => constraint.code);
  }, [dispatch, scheduleConstraints]);

  const highlightedErrors = useMemo<ScheduleErrorTimeslot[]>(() => {
    const result: ScheduleErrorTimeslot[] = [];
    scheduleViolations.forEach((error) => {
      const category: ScheduleConstraintCategory = error.category;
      if (visibleConstraintCodes?.includes(error.code)) {
        error.timeslotGroups.forEach((group) => {
          group.timeslots.forEach((timeslot) => {
            result.push({
              ...timeslot,
              category,
              code: error.code,
            });
          });
        });
      }
    });
    return result;
  }, [dispatch, scheduleViolations, visibleConstraintCodes]);

  useEffect(() => {
    dispatch(scheduleActions.setHighlightedErrors(highlightedErrors));
  }, [dispatch, highlightedErrors]);

  const toggleConstraintVisibility = useCallback(
    (code: ScheduleConstraintCode) => {
      dispatch(scheduleActions.toggleConstraintVisibility(code));
    },
    [dispatch],
  );

  const tableData = useMemo<ImprovedScheduleConstraint[]>(() => {
    return scheduleConstraints.filter((constraint) => constraint.category === 'SANPIN');
  }, [scheduleConstraints]);

  const columns = useMemo(() => {
    return getColumns(toggleConstraintVisibility);
  }, [toggleConstraintVisibility]);

  return {
    columns,
    tableData,
  };
};
